import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Scale, Plus, Trash2, Camera, Upload, Percent, Ruler, Activity, Flame } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { formatDate } from "@/lib/dates";
import { SectionHeader, EmptyState, StatCard, BmiBadge } from "@/components/Shared";
import { LineTrend } from "@/components/Charts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const RANGES = { "30d": 30, "90d": 90, "1y": 365, all: 99999 };

export default function Body() {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      <SectionHeader title={t("bodyMetrics")} />
      <Tabs defaultValue="metrics">
        <TabsList className="mb-4">
          <TabsTrigger value="metrics" data-testid="tab-metrics">{t("bodyMetrics")}</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">{t("photos")}</TabsTrigger>
        </TabsList>
        <TabsContent value="metrics"><Metrics /></TabsContent>
        <TabsContent value="photos"><Photos /></TabsContent>
      </Tabs>
    </div>
  );
}

function Metrics() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState("90d");
  const [metric, setMetric] = useState("weight");

  const { data: metrics } = useQuery({ queryKey: ["body-metrics"], queryFn: async () => (await api.get("/body-metrics")).data });
  const { data: health } = useQuery({ queryKey: ["health"], queryFn: async () => (await api.get("/profile/health")).data });

  const latest = metrics?.length ? metrics[metrics.length - 1] : null;
  const cutoff = Date.now() - RANGES[range] * 86400000;
  const filtered = (metrics || []).filter((m) => new Date(m.date).getTime() >= cutoff);
  const chartData = filtered.filter((m) => m[metric] != null).map((m) => ({ date: m.date.slice(5), value: m[metric] }));

  const METRIC_OPTS = [{ k: "weight", l: t("latestWeight"), u: "kg" }, { k: "bmi", l: "BMI", u: "" }, { k: "body_fat", l: t("bodyFat"), u: "%" }, { k: "waist", l: t("waist"), u: "cm" }];

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} className="gap-2" data-testid="log-metric-btn"><Plus className="h-4 w-4" /> {t("logMetric")}</Button>
      </div>

      {/* Latest cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Scale} label={t("latestWeight")} value={latest?.weight ?? "—"} unit="kg" testid="body-weight" />
        <StatCard icon={Activity} label="BMI" value={latest?.bmi ?? health?.bmi ?? "—"} accent="bg-sky-500/15 text-sky-500" testid="body-bmi">
          <div className="mt-2"><BmiBadge category={latest?.bmi_category ?? health?.bmi_category} t={t} /></div>
        </StatCard>
        <StatCard icon={Percent} label={t("bodyFat")} value={latest?.body_fat ?? "—"} unit={latest?.body_fat ? "%" : ""} testid="body-bf" />
        <StatCard icon={Ruler} label={t("whr")} value={latest?.whr ?? "—"} testid="body-whr" />
      </div>

      {latest && (latest.lean_mass || latest.fat_mass) && (
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={Activity} label={t("leanMass")} value={latest.lean_mass ?? "—"} unit="kg" accent="bg-primary/15 text-primary" />
          <StatCard icon={Percent} label={t("fatMass")} value={latest.fat_mass ?? "—"} unit="kg" accent="bg-orange-500/15 text-orange-500" />
        </div>
      )}

      {/* Health context */}
      {health && (
        <div className="rounded-2xl border bg-card p-5">
          <h3 className="mb-4 font-display font-semibold">{t("healthContext")}</h3>
          <div className="grid grid-cols-3 gap-4">
            <HC icon={Flame} label={t("bmr")} value={health.bmr} unit="kcal" />
            <HC icon={Activity} label={t("tdee")} value={health.tdee} unit="kcal" />
            <HC icon={Flame} label={t("calorieTarget")} value={health.calorie_target} unit="kcal" />
          </div>
          <p className="mt-4 text-xs text-muted-foreground">{t("disclaimer")}</p>
        </div>
      )}

      {/* Chart */}
      <div className="rounded-2xl border bg-card p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-40" data-testid="chart-metric"><SelectValue /></SelectTrigger>
            <SelectContent>{METRIC_OPTS.map((o) => <SelectItem key={o.k} value={o.k}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex gap-1">
            {Object.keys(RANGES).map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${range === r ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>{r}</button>
            ))}
          </div>
        </div>
        {chartData.length > 1 ? <LineTrend data={chartData} unit={METRIC_OPTS.find((o) => o.k === metric)?.u} />
          : <div className="grid h-52 place-items-center text-sm text-muted-foreground">{t("emptyMetrics")}</div>}
      </div>

      {/* History list */}
      {metrics?.length > 0 && (
        <div className="space-y-2">
          {[...metrics].reverse().slice(0, 10).map((m) => (
            <div key={m.id} className="flex items-center justify-between rounded-xl border bg-card px-4 py-3 text-sm" data-testid={`metric-${m.id}`}>
              <span className="text-muted-foreground">{formatDate(m.date, lang)}</span>
              <div className="flex items-center gap-4 tabular-nums">
                {m.weight && <span>{m.weight}kg</span>}
                {m.bmi && <span className="text-muted-foreground">BMI {m.bmi}</span>}
                {m.body_fat && <span className="text-muted-foreground">{m.body_fat}%</span>}
                <button onClick={async () => { await api.delete(`/body-metrics/${m.id}`); qc.invalidateQueries({ queryKey: ["body-metrics"] }); }} aria-label="Delete" data-testid={`metric-delete-${m.id}`} className="-my-2 -mr-2 grid h-10 w-10 place-items-center rounded-lg text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <MetricDialog open={open} onClose={() => setOpen(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ["body-metrics"] }); qc.invalidateQueries({ queryKey: ["health"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); }} />
    </div>
  );
}

const HC = ({ icon: Icon, label, value, unit }) => (
  <div className="text-center">
    <Icon className="mx-auto h-4 w-4 text-muted-foreground" />
    <div className="mt-1 font-display text-2xl font-bold tabular-nums">{value ?? "—"}</div>
    <div className="text-[11px] text-muted-foreground">{label} ({unit})</div>
  </div>
);

function MetricField({ label, unit, value, onChange, testid }) {
  return (
    <div><Label className="text-xs">{label} ({unit})</Label>
      <Input type="number" step="0.1" inputMode="decimal" value={value ?? ""} onChange={onChange} className="mt-1 tabular-nums" data-testid={testid} /></div>
  );
}

function MetricDialog({ open, onClose, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({});
  const upd = (k) => (e) => setF((prev) => ({ ...prev, [k]: parseFloat(e.target.value) || undefined }));
  const save = async () => {
    if (!Object.keys(f).length) return toast.error("Enter at least one value");
    await api.post("/body-metrics", f);
    toast.success(t("save")); onSaved(); onClose(); setF({});
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t("logMetric")}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] space-y-4 overflow-y-auto no-scrollbar pr-1">
          <div className="grid grid-cols-2 gap-3">
            <MetricField label={t("latestWeight")} unit="kg" value={f.weight} onChange={upd("weight")} testid="metric-weight" />
            <MetricField label={t("bodyFat")} unit="%" value={f.body_fat} onChange={upd("body_fat")} testid="metric-body_fat" />
          </div>
          <div className="text-xs font-semibold uppercase text-muted-foreground">{t("circumferences")}</div>
          <div className="grid grid-cols-3 gap-3">
            <MetricField label={t("neck")} unit="cm" value={f.neck} onChange={upd("neck")} testid="metric-neck" />
            <MetricField label={t("chest")} unit="cm" value={f.chest} onChange={upd("chest")} testid="metric-chest" />
            <MetricField label={t("waist")} unit="cm" value={f.waist} onChange={upd("waist")} testid="metric-waist" />
            <MetricField label={t("hips")} unit="cm" value={f.hips} onChange={upd("hips")} testid="metric-hips" />
            <MetricField label={t("bicep")} unit="cm" value={f.bicep} onChange={upd("bicep")} testid="metric-bicep" />
            <MetricField label={t("thigh")} unit="cm" value={f.thigh} onChange={upd("thigh")} testid="metric-thigh" />
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-metric">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Photos ---------------- */
function Photos() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [pose, setPose] = useState("front");
  const [compare, setCompare] = useState({ before: null, after: null });

  const { data: photos } = useQuery({ queryKey: ["photos"], queryFn: async () => (await api.get("/photos")).data });

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { e.target.value = ""; return toast.error("File tidak valid. Unggah gambar (JPG, PNG, atau WebP)."); }
    if (file.size > 3 * 1024 * 1024) { e.target.value = ""; return toast.error("Max 3MB"); }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/photos", { image: reader.result, pose });
        toast.success(t("save")); qc.invalidateQueries({ queryKey: ["photos"] });
      } catch (err) {
        toast.error(formatError(err.response?.data?.detail) || err.message);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select value={pose} onValueChange={setPose}>
          <SelectTrigger className="w-32" data-testid="photo-pose"><SelectValue /></SelectTrigger>
          <SelectContent>{["front", "side", "back"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="photo-input" />
        <Button onClick={() => fileRef.current?.click()} className="gap-2" data-testid="upload-photo-btn"><Upload className="h-4 w-4" /> {t("uploadPhoto")}</Button>
      </div>

      {compare.before && compare.after && (
        <BeforeAfter before={compare.before} after={compare.after} onClear={() => setCompare({ before: null, after: null })} label={t("beforeAfter")} />
      )}

      {photos?.length ? (
        <>
          <p className="text-xs text-muted-foreground">{t("beforeAfter")}: {compare.before ? "✓" : "?"} → {compare.after ? "✓" : "?"}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((p) => (
              <div key={p.id} tabIndex={0} className="group relative overflow-hidden rounded-2xl border outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`photo-${p.id}`}>
                <img src={p.image} alt={p.pose} className="aspect-[3/4] w-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent p-2 text-xs text-white">
                  <span>{formatDate(p.date, lang)} · {p.pose}</span>
                </div>
                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  <Button size="sm" variant="secondary" onClick={() => setCompare((c) => ({ ...c, before: p.image }))}>A</Button>
                  <Button size="sm" variant="secondary" onClick={() => setCompare((c) => ({ ...c, after: p.image }))}>B</Button>
                  <Button size="sm" variant="destructive" onClick={async () => { await api.delete(`/photos/${p.id}`); qc.invalidateQueries({ queryKey: ["photos"] }); }}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <EmptyState icon={Camera} title={t("photos")} description={t("startTracking")} />
      )}
    </div>
  );
}

function BeforeAfter({ before, after, onClear, label }) {
  const [pos, setPos] = useState(50);
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="font-display font-semibold">{label}</span>
        <button onClick={onClear} className="text-xs text-muted-foreground hover:text-destructive">Clear</button>
      </div>
      <div className="relative mx-auto aspect-[3/4] max-w-sm select-none overflow-hidden rounded-xl">
        <img src={after} alt="after" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={before} alt="before" className="h-full w-full object-cover" style={{ width: "100vw", maxWidth: "24rem" }} />
        </div>
        <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${pos}%` }} />
        <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(e.target.value)}
          className="absolute inset-x-0 bottom-4 mx-auto w-4/5 accent-primary" data-testid="ba-slider" />
      </div>
    </div>
  );
}
